import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { API } from '@/App';
import { toast } from 'sonner';

const SEVERITY_COLORS = {
  critical: { bg: 'bg-kova-red/10', border: 'border-kova-red/40', text: 'text-kova-red', dot: 'bg-kova-red' },
  warning:  { bg: 'bg-kova-gold/10', border: 'border-kova-gold/40', text: 'text-kova-gold', dot: 'bg-kova-gold' },
  info:     { bg: 'bg-kova-cyan/10', border: 'border-kova-cyan/40', text: 'text-kova-cyan', dot: 'bg-kova-cyan' },
};

const formatTs = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

/* ═══════════════════════════════════════
   ACTIVE ALERTS TAB
   ═══════════════════════════════════════ */
function ActiveAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/alerts?limit=50`, { credentials: 'include' });
      if (res.ok) setAlerts(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (alertId) => {
    await fetch(`${API}/alerts/${alertId}/read`, { method: 'POST', credentials: 'include' });
    setAlerts(prev => prev.map(a => a.alert_id === alertId ? { ...a, is_read: true } : a));
  };

  const markAllRead = async () => {
    await fetch(`${API}/alerts/read-all`, { method: 'POST', credentials: 'include' });
    setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
    toast.success('All alerts marked as read');
  };

  if (loading) return <div className="text-kova-slate font-mono text-sm p-8 text-center">Loading alerts...</div>;

  return (
    <div data-testid="active-alerts-tab">
      {alerts.length > 0 && (
        <div className="flex justify-end px-4 pt-3">
          <button
            onClick={markAllRead}
            className="text-[10px] font-mono text-kova-cyan uppercase tracking-wider hover:underline"
            data-testid="mark-all-read-btn"
          >
            Mark all read
          </button>
        </div>
      )}
      {alerts.length === 0 ? (
        <div className="p-12 text-center">
          <p className="text-kova-slate font-mono text-sm">No alerts yet</p>
          <p className="text-kova-slate font-mono text-xs mt-2">Alerts are generated when API calls fail or thresholds are exceeded</p>
        </div>
      ) : (
        <div className="divide-y divide-kova-cyan/12">
          {alerts.map((alert) => {
            const sev = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
            return (
              <div
                key={alert.alert_id}
                className={`flex items-start gap-3 px-4 py-3 transition-colors ${alert.is_read ? 'opacity-60' : 'hover:bg-kova-card/50'}`}
                data-testid={`alert-item-${alert.alert_id}`}
              >
                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sev.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{alert.title}</span>
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase tracking-wider ${sev.bg} ${sev.text} border ${sev.border}`}>
                      {alert.severity}
                    </span>
                    {alert.notified_slack && <span className="text-[9px] text-kova-slate" title="Sent to Slack">S</span>}
                    {alert.notified_email && <span className="text-[9px] text-kova-slate" title="Sent via Email">E</span>}
                  </div>
                  <p className="text-xs text-kova-slate mt-0.5 truncate">{alert.message}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-kova-slate font-mono">{formatTs(alert.created_at)}</span>
                    {alert.connection_name && <span className="text-[10px] text-kova-cyan font-mono">{alert.connection_name}</span>}
                  </div>
                </div>
                {!alert.is_read && (
                  <button
                    onClick={() => markRead(alert.alert_id)}
                    className="text-[10px] text-kova-slate hover:text-kova-cyan font-mono uppercase tracking-wider flex-shrink-0"
                    data-testid={`mark-read-${alert.alert_id}`}
                  >
                    Dismiss
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   NOTIFICATION HISTORY TAB
   ═══════════════════════════════════════ */
function NotificationHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/notifications/history?limit=50`, { credentials: 'include' });
      if (res.ok) setHistory(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleResend = async (historyId) => {
    setResending(historyId);
    try {
      const res = await fetch(`${API}/notifications/${historyId}/resend`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`Resent to ${data.destination}`);
        } else {
          toast.error('Resend failed — check your notification settings');
        }
        fetchHistory(); // refresh table
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Resend failed');
      }
    } catch (e) {
      toast.error('Resend request failed');
    } finally {
      setResending(null);
    }
  };

  if (loading) return <div className="text-kova-slate font-mono text-sm p-8 text-center">Loading history...</div>;

  return (
    <div data-testid="notification-history-tab">
      {history.length === 0 ? (
        <div className="p-12 text-center">
          <p className="text-kova-slate font-mono text-sm">No notification history</p>
          <p className="text-kova-slate font-mono text-xs mt-2">Delivery receipts appear here when notifications are dispatched</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {/* Table Header */}
          <div className="grid grid-cols-[130px_1fr_70px_1fr_110px_60px_90px] gap-2 px-4 py-2 bg-kova-card border-b border-kova-cyan/12 text-[9px] font-mono text-kova-slate uppercase tracking-[2px]">
            <span>Timestamp</span>
            <span>Alert</span>
            <span>Channel</span>
            <span>Destination</span>
            <span>Status</span>
            <span>Retries</span>
            <span></span>
          </div>
          {/* Table Body */}
          <div className="divide-y divide-kova-card2">
            {history.map((row) => {
              const isFailed = row.status === 'delivery_failed';
              return (
                <div
                  key={row.history_id}
                  className="grid grid-cols-[130px_1fr_70px_1fr_110px_60px_90px] gap-2 px-4 py-2.5 items-center text-xs hover:bg-kova-card/40 transition-colors"
                  data-testid={`history-row-${row.history_id}`}
                >
                  {/* Timestamp */}
                  <span className="text-[10px] text-kova-slate font-mono">{formatTs(row.created_at)}</span>
                  {/* Alert name */}
                  <span className="text-kova-silv truncate" title={row.alert_name}>{row.alert_name}</span>
                  {/* Channel icon */}
                  <span className="flex items-center gap-1.5">
                    {row.channel === 'slack' ? (
                      <span title="Slack" className="text-kova-cyan font-mono text-[10px] font-semibold tracking-wide">SLK</span>
                    ) : (
                      <span title="Email" className="text-kova-gold font-mono text-[10px] font-semibold tracking-wide">EML</span>
                    )}
                  </span>
                  {/* Destination */}
                  <span className="text-kova-slate truncate font-mono text-[10px]" title={row.destination}>{row.destination}</span>
                  {/* Status */}
                  <span>
                    {isFailed ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-kova-red/15 border border-kova-red/40 text-kova-red text-[9px] font-mono font-bold uppercase tracking-wider" data-testid={`failed-badge-${row.history_id}`}>
                        FAILED
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono text-kova-green uppercase tracking-wider">Delivered</span>
                    )}
                  </span>
                  {/* Retry count */}
                  <span className="text-[10px] font-mono text-kova-slate text-center">{row.retry_count || 0}</span>
                  {/* Resend button */}
                  <span>
                    {isFailed && (
                      <button
                        onClick={() => handleResend(row.history_id)}
                        disabled={resending === row.history_id}
                        className="text-[10px] font-mono font-semibold text-kova-cyan uppercase tracking-wider hover:underline disabled:opacity-40 disabled:cursor-wait"
                        data-testid={`resend-btn-${row.history_id}`}
                      >
                        {resending === row.history_id ? 'Sending...' : 'Resend'}
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   ALERTS PAGE (TABBED)
   ═══════════════════════════════════════ */
export default function Alerts() {
  const [tab, setTab] = useState('active');

  return (
    <div className="space-y-4" data-testid="alerts-page">
      {/* Tab bar */}
      <div className="flex items-center gap-0 border-b border-kova-cyan/12">
        <button
          onClick={() => setTab('active')}
          className={`px-5 py-3 text-xs font-mono uppercase tracking-[2px] border-b-2 transition-colors ${
            tab === 'active'
              ? 'text-kova-cyan border-kova-cyan'
              : 'text-kova-slate border-transparent hover:text-kova-silv'
          }`}
          data-testid="tab-active-alerts"
        >
          Active Alerts
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-5 py-3 text-xs font-mono uppercase tracking-[2px] border-b-2 transition-colors ${
            tab === 'history'
              ? 'text-kova-cyan border-kova-cyan'
              : 'text-kova-slate border-transparent hover:text-kova-silv'
          }`}
          data-testid="tab-notification-history"
        >
          Notification History
        </button>
      </div>

      {/* Tab content */}
      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="card-brutal overflow-hidden"
      >
        {tab === 'active' ? <ActiveAlerts /> : <NotificationHistory />}
      </motion.div>
    </div>
  );
}
